// Ask — a floating assistant pinned to the bottom-right of every page.
//
// Deliberately NOT a page: the questions it answers ("which reseller grew most
// last month?") come up WHILE looking at a chart, so it has to be reachable
// without navigating away and losing the filters you were looking at.
//
// The takeaway sentence is always shown with the numbers it came from. An AI
// summary of billing data that nobody can check is worse than no summary, so
// the rows and the filters the model chose stay on screen next to it.
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { isAdminOrAbove } from '../../lib/roles';
import { formatNumber } from '../../utils/formatters';
import { api } from '../../lib/api';

// The mark: a magnifier with a spark at its shoulder — "search, with something
// extra". The magnifier says immediately that you can look things up here,
// which a bare sparkle never did; the spark is what marks it as the AI one.
// Drawn on a 24px grid at 1.9 stroke so it holds together down to 16px.
const AskMark = ({ className = 'h-5 w-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="10" cy="11" r="5.6" stroke="currentColor" strokeWidth="1.9" />
    <path
      d="M14.1 15.1L19.4 20.4"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
    />
    {/* Filled so it reads as a highlight rather than another outline shape. */}
    <path
      d="M18.4 2.3l.85 2.15 2.15.85-2.15.85-.85 2.15-.85-2.15-2.15-.85 2.15-.85.85-2.15z"
      fill="currentColor"
    />
  </svg>
);

const CloseIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M6 6l8 8M14 6l-8 8" strokeLinecap="round" />
  </svg>
);

const SendIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M3 10l14-6-6 14-2-6-6-2z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Fallback starters, used only until /api/ask/status responds with the real
// list. Kept in step with STARTER_QUESTIONS in ask.service.js.
const EXAMPLES = [
  'Which resellers used the most last month?',
  'Show usage by month this year',
  'Top 5 products overall',
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ---------------------------------------------------------------- dates -----
// The API speaks ISO ("2026-08-23", "2026-08") because that is what BigQuery
// returns and what the spec round-trips. Nobody reads dates that way, so they
// are reformatted for display here — parsed as plain strings, never through
// `new Date()`, which would drag the viewer's timezone into a value that has no
// time component and can shift it a whole day.

const parseIso = (s) => {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(String(s || ''));
  if (!m) return null;
  return { year: +m[1], month: +m[2], day: m[3] ? +m[3] : null };
};

// "2026-08-23" -> "23 Aug 2026"
const prettyDate = (s, { withYear = true } = {}) => {
  const p = parseIso(s);
  if (!p) return s;
  const mon = MONTHS[p.month - 1] || '';
  const year = withYear ? ` ${p.year}` : '';
  return p.day ? `${p.day} ${mon}${year}` : `${mon}${year}`;
};

// A row label from a time-series query. Granularity decides the shape:
//   day   "2026-08-23" -> "23 Aug"
//   month "2026-08"    -> "Aug 2026"
//   year  "2026"       -> "2026"
const prettyPeriod = (label, granularity) => {
  if (granularity === 'year') return String(label);
  const p = parseIso(label);
  if (!p) return String(label);
  if (granularity === 'day') return `${p.day} ${MONTHS[p.month - 1]}`;
  return `${MONTHS[p.month - 1]} ${p.year}`;
};

// Days in a month, so a range covering a whole calendar month can be named as
// that month rather than spelled out end to end.
const daysInMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();
const isWholeMonth = (a, b) => (
  a && b && a.year === b.year && a.month === b.month
  && a.day === 1 && b.day === daysInMonth(a.year, a.month)
);

// Describe a range in the shortest form that is still true. The shape adapts to
// what the range actually IS, rather than always printing both endpoints —
// "26 – 26 Aug 2026" is noise where "26 Aug 2026" says the same thing.
//
//   one day       -> "26 Aug 2026"
//   whole month   -> "Aug 2026"
//   part of month -> "1 – 26 Aug 2026"
//   same year     -> "5 Jun – 26 Aug 2026"
//   otherwise     -> "5 Jun 2025 – 26 Aug 2026"
const prettyRange = (from, to) => {
  const a = parseIso(from);
  const b = parseIso(to);
  if (!a && !b) return null;
  if (!a) return `up to ${prettyDate(to)}`;
  if (!b) return `from ${prettyDate(from)}`;
  if (from === to) return prettyDate(from);
  if (isWholeMonth(a, b)) return `${MONTHS[a.month - 1]} ${a.year}`;
  if (a.year === b.year && a.month === b.month && a.day && b.day) {
    return `${a.day} – ${b.day} ${MONTHS[b.month - 1]} ${b.year}`;
  }
  if (a.year === b.year) return `${prettyDate(from, { withYear: false })} – ${prettyDate(to)}`;
  return `${prettyDate(from)} – ${prettyDate(to)}`;
};

// Turn the validated spec into readable chips, so the reader can see WHAT was
// measured instead of trusting the sentence.
function describeSpec(spec) {
  if (!spec) return [];
  const bits = [spec.metric === 'total' ? 'Total usage' : 'Avg / active day'];
  if (spec.groupBy && spec.groupBy !== 'none') {
    bits.push(`by ${spec.groupBy === 'period' ? spec.granularity : spec.groupBy}`);
  }
  if (spec.reseller) bits.push(spec.reseller);
  if (spec.productFamily) bits.push(spec.productFamily);
  if (spec.product) bits.push(spec.product);
  const range = prettyRange(spec.from, spec.to);
  if (range) bits.push(range);
  return bits;
}

// ------------------------------------------------------- follow-up ideas ----
// Suggestions are derived from the result itself, not from another model call:
// instant, free, and guaranteed to be about what you are actually looking at.
//
// Each suggestion must stand ALONE, because the server answers every question
// independently — there is no conversation memory. So the dates and names are
// spelled out in full rather than said as "that period" or "them".

// The same range, phrased as a clause that can sit inside a question. It has to
// read naturally AND survive being parsed back by the spec model, so it uses
// ordinary prepositions rather than a dash: "on 26 Aug 2026", "in Aug 2026".
function periodPhrase(spec) {
  const a = parseIso(spec?.from);
  const b = parseIso(spec?.to);
  if (!a && !b) return '';
  if (!a) return ` up to ${prettyDate(spec.to)}`;
  if (!b) return ` since ${prettyDate(spec.from)}`;
  if (spec.from === spec.to) return ` on ${prettyDate(spec.from)}`;
  if (isWholeMonth(a, b)) return ` in ${MONTHS[a.month - 1]} ${a.year}`;
  return ` from ${prettyDate(spec.from)} to ${prettyDate(spec.to)}`;
}

// Each suggestion carries BOTH the wording shown on the chip and the exact spec
// it means. Sending the spec lets the server skip the question->spec model call
// (~1.2s) — we generated the chip, so we already know precisely what it asks.
// The wording still travels, so the transcript reads naturally.
function followUps(spec, rows) {
  if (!spec || !rows?.length) return [];
  const top = rows[0]?.label;
  const when = periodPhrase(spec);
  const dates = { from: spec.from, to: spec.to };
  const out = [];

  // Only the fields that differ from the current spec are overridden; the rest
  // (dates, filters) carry forward, which is what makes these read as follow-ups.
  const derive = (text, patch) => out.push({
    text,
    spec: {
      metric: 'avg_per_active_day',
      granularity: spec.granularity || 'month',
      sort: 'desc',
      topN: 20,
      reseller: null,
      productFamily: null,
      product: null,
      ...dates,
      ...patch,
    },
  });

  switch (spec.groupBy) {
    case 'reseller':
      if (top) derive(`Which products does ${top} use most${when}?`, { groupBy: 'product', reseller: top });
      if (top) derive(`Show ${top} usage by month`, { groupBy: 'period', granularity: 'month', reseller: top, from: null, to: null });
      derive(`Which product families were used most${when}?`, { groupBy: 'family' });
      break;
    case 'product':
    case 'family': {
      const key = spec.groupBy === 'family' ? 'productFamily' : 'product';
      if (top) derive(`Which resellers use ${top} most${when}?`, { groupBy: 'reseller', [key]: top });
      if (top) derive(`Show ${top} usage by month`, { groupBy: 'period', granularity: 'month', [key]: top, from: null, to: null });
      derive(`Which resellers used the most${when}?`, { groupBy: 'reseller' });
      break;
    }
    case 'period':
      derive(`Which resellers used the most${when}?`, { groupBy: 'reseller' });
      derive(`Top 5 products${when}`, { groupBy: 'product', topN: 5 });
      break;
    default:
      derive(`Break that down by product family${when}`, { groupBy: 'family' });
      derive(`Which resellers used the most${when}?`, { groupBy: 'reseller' });
      break;
  }

  // The other metric — a fair per-day comparison vs. the volume actually
  // billed. The grouping is restated so the swap does not collapse the view.
  const GROUPED_AS = {
    reseller: 'by reseller', product: 'by product', family: 'by product family', period: 'by month',
  };
  const grouped = GROUPED_AS[spec.groupBy] || '';
  const other = spec.metric === 'total' ? 'avg_per_active_day' : 'total';
  out.push({
    text: `Show the ${other === 'total' ? 'total' : 'average per active day'} ${grouped}${when}`.replace(/\s{2,}/g, ' '),
    spec: {
      ...spec, metric: other, answerable: undefined,
    },
  });

  return out.slice(0, 3);
}

const ROW_LIMIT = 10;

// ⌘ on Apple hardware, Ctrl everywhere else. Guarded because this module is
// also parsed during the production build, where `navigator` does not exist.
const isMac = typeof navigator !== 'undefined'
  && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');

// One answered question. Rendered the same whether it is the newest turn or one
// scrolled back to, so the panel reads as a transcript.
function Turn({ turn, onExpand, onSuggest, showSuggestions }) {
  const {
    question, answer, rows = [], spec, error, errorCode, rowLimit = ROW_LIMIT,
  } = turn;
  // "I can't answer that from this data" is a legitimate answer, not a fault.
  // Red is for things that actually broke — a rejected key, a timeout — so a
  // considered refusal gets a neutral, informational treatment instead.
  const isRefusal = errorCode === 'not_answerable' || errorCode === 'unknown_value';
  const rowMax = Math.max(...rows.map((r) => Math.abs(r.value)), 1);
  const isSeries = spec?.groupBy === 'period';

  return (
    <div className="space-y-2.5">
      <div className="flex justify-end">
        <p className="max-w-[85%] rounded-2xl rounded-br-md bg-brand/10 px-3.5 py-2 text-sm text-brand dark:bg-brand/20 dark:text-brand-light">
          {question}
        </p>
      </div>

      {error ? (
        <div className={`flex gap-2 rounded-xl px-3 py-2.5 ${isRefusal ? 'bg-slate-100/70 dark:bg-white/5' : 'bg-red-50 dark:bg-red-500/10'}`}>
          <svg
            className={`mt-0.5 h-4 w-4 shrink-0 ${isRefusal ? 'text-slate-400' : 'text-red-500'}`}
            viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"
          >
            <circle cx="10" cy="10" r="7.5" />
            {isRefusal
              ? <path d="M10 9.5v4.5M10 6.5h.01" strokeLinecap="round" />
              : <path d="M10 6.5v4M10 13.5h.01" strokeLinecap="round" />}
          </svg>
          <p className={`text-sm leading-relaxed ${isRefusal ? 'text-slate-600 dark:text-slate-300' : 'text-red-700 dark:text-red-300'}`}>
            {error}
          </p>
        </div>
      ) : (
        <>
          {/* The takeaway, marked with a brand rule down its left edge so the
              one interpreted sentence is visually distinct from the measured
              figures below it. */}
          <div className="border-l-2 border-brand/40 pl-3">
            {answer === null ? (
              /* Rows are already on screen; the takeaway is still being written.
                 A shimmer holds its place so the layout does not jump. */
              <div className="space-y-1.5 py-1" aria-label="Writing the takeaway">
                <div className="h-2.5 w-11/12 animate-pulse rounded bg-slate-200/70 dark:bg-white/10" />
                <div className="h-2.5 w-2/3 animate-pulse rounded bg-slate-200/70 dark:bg-white/10" />
              </div>
            ) : (
              <p className="ask-answer-in text-[15px] font-medium leading-relaxed text-navy-800 dark:text-slate-100">{answer}</p>
            )}
          </div>

          <div className="flex flex-wrap gap-1">
            {describeSpec(spec).map((bit) => (
              <span key={bit} className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500 dark:bg-white/10 dark:text-slate-400">
                {bit}
              </span>
            ))}
          </div>

          {rows.length ? (
            <>
              <div className="overflow-hidden rounded-xl border border-slate-900/5 dark:border-white/5">
                {rows.slice(0, rowLimit).map((r, i) => {
                  // Proportional bar behind each row: turns a column of numbers
                  // into a shape readable at a glance — the job the sentence
                  // deliberately no longer does. Scaled against the largest
                  // value in the WHOLE result, so expanding never rescales.
                  const pct = Math.max(2, Math.round((Math.abs(r.value) / rowMax) * 100));
                  return (
                    <div
                      key={r.label}
                      className="relative flex items-center gap-2.5 border-b border-slate-900/5 px-2.5 py-[7px] last:border-0 dark:border-white/5"
                    >
                      <span
                        className="absolute inset-y-0 left-0 bg-brand/[0.09] transition-all duration-500 dark:bg-brand/20"
                        style={{ width: `${pct}%` }}
                        aria-hidden="true"
                      />
                      {/* Rank column. Suppressed on a time series, where the
                          first row is the oldest rather than the biggest. */}
                      {!isSeries && (
                        <span className={`relative w-3.5 shrink-0 text-right text-[10px] font-semibold tabular-nums ${i === 0 ? 'text-brand' : 'text-slate-300 dark:text-slate-600'}`}>
                          {i + 1}
                        </span>
                      )}
                      <span className="relative min-w-0 flex-1 truncate text-sm text-slate-600 dark:text-slate-300">
                        {isSeries ? prettyPeriod(r.label, spec?.granularity) : r.label}
                      </span>
                      <span className="relative shrink-0 text-sm font-semibold tabular-nums text-navy-800 dark:text-slate-100">
                        {formatNumber(r.value)}
                      </span>
                    </div>
                  );
                })}
              </div>

              {rows.length > rowLimit && (
                <button
                  type="button"
                  onClick={onExpand}
                  className="w-full rounded-lg py-1.5 text-xs font-medium text-brand transition hover:bg-brand/5"
                >
                  Show all {rows.length} rows
                </button>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-400 dark:text-slate-500">No usage data matched that question.</p>
          )}

          {showSuggestions && answer !== null && followUps(spec, rows).length > 0 && (
            <div className="space-y-1 pt-0.5">
              <p className="flex items-center gap-1.5 px-0.5 text-xs text-slate-400 dark:text-slate-500">
                <span className="h-px w-3 shrink-0 bg-slate-300 dark:bg-slate-600" aria-hidden="true" />
                Ask next, or type your own
              </p>
              <div className="flex flex-wrap gap-1.5">
                {followUps(spec, rows).map((q) => (
                  <button
                    key={q.text}
                    type="button"
                    onClick={() => onSuggest(q)}
                    className="rounded-full border border-brand/20 bg-brand/5 px-3 py-1.5 text-xs text-brand transition hover:border-brand/40 hover:bg-brand/10 dark:border-brand/30 dark:bg-brand/10 dark:text-brand-light dark:hover:bg-brand/20"
                  >
                    {q.text}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function AskWidget() {
  const { profile } = useAuth();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState([]);   // the transcript, oldest first
  const [pending, setPending] = useState(null); // question awaiting an answer
  const [busy, setBusy] = useState(false);
  const [configured, setConfigured] = useState(null); // null = not checked yet
  const [examples, setExamples] = useState(EXAMPLES);
  const inputRef = useRef(null);
  const bodyRef = useRef(null);
  const panelRef = useRef(null);
  const launcherRef = useRef(null);
  // Bumped on every close. A request already in flight compares against this
  // when it lands, so a reply to a discarded session cannot reappear in the
  // fresh transcript the next time the panel is opened.
  const sessionRef = useRef(0);
  const wipeRef = useRef(null);

  const isAdmin = isAdminOrAbove(profile);

  // Closing ends the session: the transcript, the draft question and any
  // in-flight reply are all discarded, so re-opening always starts clean.
  //
  // The wipe waits for the close animation. The panel fades over ~200ms and is
  // hidden rather than unmounted, so clearing immediately would blank its
  // contents mid-fade — the session is already invalid from the bump above.
  const close = useCallback(() => {
    sessionRef.current += 1;
    setOpen(false);
    setBusy(false);
    clearTimeout(wipeRef.current);
    wipeRef.current = setTimeout(() => {
      setTurns([]);
      setPending(null);
      setQuestion('');
    }, 220);
  }, []);

  // Check configuration once, and only when the panel is first opened — no
  // reason to spend a request on every page load for a panel nobody opens.
  useEffect(() => {
    if (!open || configured !== null) return undefined;
    let alive = true;
    api.get('/api/ask/status')
      .then((r) => {
        if (!alive) return;
        setConfigured(!!r.configured);
        // Served by the API so the widget offers exactly the questions the
        // server pre-computed at warm-up — a hard-coded list here would drift
        // and quietly stop being instant.
        if (Array.isArray(r.examples) && r.examples.length) setExamples(r.examples);
      })
      .catch(() => { if (alive) setConfigured(false); });
    return () => { alive = false; };
  }, [open, configured]);

  useEffect(() => {
    if (!open) return;
    // Opening always starts a clean session. If the panel is reopened before the
    // deferred wipe has run, do it NOW rather than cancelling it — cancelling
    // would leave the previous transcript on screen.
    clearTimeout(wipeRef.current);
    setTurns([]);
    setPending(null);
    setQuestion('');
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => () => clearTimeout(wipeRef.current), []);

  // Keep the newest turn in view as the transcript grows.
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, pending]);

  // Esc closes; ⌘K / Ctrl+K opens from anywhere on the page. The shortcut is
  // bound whether or not the panel is open, so it works as a way IN — the whole
  // point of it. Typing in another field still gets ⌘K, which is the convention
  // people expect from a command palette.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        // Must go through close() when shutting, not setOpen(false) — closing
        // ends the session (transcript, draft, in-flight reply), and every exit
        // route has to behave the same way.
        if (open) close(); else setOpen(true);
        return;
      }
      if (e.key === 'Escape' && open) close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  // Click anywhere outside the panel closes it. The launcher is treated as
  // "inside" so its own click is not counted as an outside click, which would
  // close and immediately re-open. Bound on mousedown so a click that starts
  // inside and drags out (selecting text) does not dismiss the panel.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (launcherRef.current?.contains(e.target)) return;
      close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  const submit = useCallback(async (e, override) => {
    e?.preventDefault();
    // `override` lets a suggestion chip ask immediately — going through state
    // would need an extra render before the value was readable here. It may be
    // a plain string (typed question, starter) or { text, spec }: a chip we
    // generated, which already knows the exact query it means and so lets the
    // server skip the question->spec model call.
    const known = override && typeof override === 'object' ? override : null;
    const q = String(known ? known.text : (override ?? question)).trim();
    if (!q || busy) return;

    // Clear the box immediately: the question moves into the transcript, so
    // leaving it here would make every follow-up a delete-first job.
    setQuestion('');
    setPending(q);
    setBusy(true);
    const session = sessionRef.current;
    const alive = () => sessionRef.current === session;

    try {
      // Send the last couple of turns so a fragment ("yesterday", "and for
      // Cove?") resolves against what was just asked. Only the question and the
      // resolved spec travel — never the rows.
      const history = turns
        .filter((t) => t.spec)
        .slice(-2)
        .map((t) => ({ question: t.question, spec: t.spec }));

      // Two phases. The rows arrive as soon as the query returns and are shown
      // straight away; the takeaway sentence lands a model call later and is
      // patched into the same turn. Waiting for both would add ~1.5s before
      // anything appeared.
      await api.stream('/api/ask', { question: q, history, spec: known?.spec }, (ev) => {
        if (!alive()) return; // panel was closed — drop it

        if (ev.type === 'rows') {
          setPending(null);
          setTurns((prev) => [...prev, {
            question: ev.question,
            spec: ev.spec,
            rows: ev.rows,
            answer: null, // still coming
            rowLimit: ROW_LIMIT,
          }]);
        } else if (ev.type === 'answer') {
          setTurns((prev) => prev.map(
            (t, i) => (i === prev.length - 1 ? { ...t, answer: ev.answer } : t),
          ));
        } else if (ev.type === 'error') {
          // Failed after the rows were sent — attach it to the same turn.
          setTurns((prev) => prev.map(
            (t, i) => (i === prev.length - 1 ? { ...t, answer: '', error: ev.error, errorCode: ev.code } : t),
          ));
        }
      });
    } catch (err) {
      // Failures join the transcript too, attached to the question that caused
      // them — otherwise the error would vanish on the next question.
      if (alive()) {
        setTurns((prev) => [...prev, {
          question: q,
          error: err.message || 'Something went wrong.',
          errorCode: err.code,
          rows: [],
        }]);
      }
    } finally {
      if (alive()) {
        setPending(null);
        setBusy(false);
        inputRef.current?.focus();
      }
    }
  }, [question, busy, turns]);

  const expandTurn = (i) => setTurns((prev) => prev.map(
    (t, idx) => (idx === i ? { ...t, rowLimit: t.rows.length } : t),
  ));

  // Never render for resellers. (Cosmetic only — the API is admin-gated too.)
  if (!isAdmin) return null;

  const empty = turns.length === 0 && !pending;

  return (
    <>
      {/* Launcher — the mark plus the word "Ask", inside a drifting gradient
          ring. The ring is the app's own brand ramp with a cyan waypoint
          borrowed from the dashboard's aurora background, and its slow movement
          marks this as the one live/AI affordance on an otherwise static page.
          The label means the button explains itself, so no hover tooltip is
          needed — the ⌘K shortcut is taught inside the panel instead. */}
      <div
        className={`group fixed bottom-5 right-5 z-40 transition-all duration-200 ${
          open ? 'pointer-events-none translate-y-1 scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
      >
        {/* Glow, behind everything and never interactive. */}
        <span
          className="ask-border pointer-events-none absolute -inset-1.5 -z-10 rounded-full opacity-40 blur-lg transition-opacity duration-300 group-hover:opacity-70"
          aria-hidden="true"
        />
        <button
          ref={launcherRef}
          type="button"
          onClick={() => (open ? close() : setOpen(true))}
          aria-label="Ask about your usage data"
          className="ask-border relative flex rounded-full p-[1.5px] shadow-brand transition-transform duration-200 hover:-translate-y-0.5 active:scale-95"
        >
          {/* Inner surface, inset by the 1.5px padding above — that gap IS the
              gradient ring. Opaque so the gradient never shows through. */}
          <span className="flex items-center gap-2 rounded-full bg-white py-3 pl-[18px] pr-5 text-navy-800 dark:bg-navy-900 dark:text-white">
            <AskMark className="h-[20px] w-[20px] text-brand transition-transform duration-200 group-hover:scale-110 dark:text-brand-light" />
            <span className="text-sm font-semibold tracking-tight">Ask</span>
          </span>
        </button>
      </div>

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Ask about your usage data"
        aria-hidden={!open}
        className={`fixed bottom-5 right-5 z-40 flex w-[calc(100vw-2.5rem)] max-w-[30rem] origin-bottom-right flex-col overflow-hidden rounded-3xl border border-white/60 bg-white/95 shadow-card-hover ring-1 ring-slate-900/5 backdrop-blur-2xl transition-all duration-200 ease-out dark:border-white/10 dark:bg-slate-900/95 dark:ring-white/10 ${
          open ? 'pointer-events-auto translate-y-0 scale-100 opacity-100' : 'pointer-events-none translate-y-2 scale-95 opacity-0'
        }`}
        style={{ maxHeight: 'min(42rem, calc(100vh - 2.5rem))' }}
      >
        {/* Header. A hairline of brand gradient across the very top ties the
            panel to the launcher it grew out of. */}
        <div className="relative shrink-0 border-b border-slate-900/5 dark:border-white/10">
          <span className="ask-border absolute inset-x-0 top-0 h-[3px]" aria-hidden="true" />
          <div className="flex items-center justify-between gap-2 px-4 pb-3 pt-3.5">
            <div className="flex min-w-0 items-center gap-2.5">
              {/* Same treatment as the launcher — gradient ring around an
                  opaque surface — so the panel is visibly the thing the button
                  opened, rather than a differently-styled cousin of it. */}
              <span className="ask-border flex shrink-0 rounded-full p-[1.5px]">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white dark:bg-navy-900">
                  <AskMark className="h-[17px] w-[17px] text-brand dark:text-brand-light" />
                </span>
              </span>
              <div className="min-w-0 leading-tight">
                <p className="text-base font-semibold text-navy-800 dark:text-slate-100">Ask</p>
                <p className="truncate text-xs text-slate-400 dark:text-slate-500">
                  Anything about your usage data
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-0.5">
              {turns.length > 0 && (
                <button
                  type="button"
                  onClick={() => setTurns([])}
                  className="rounded-lg px-2 py-1 text-xs font-medium text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10 dark:hover:text-slate-300"
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10 dark:hover:text-slate-300"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>

        {/* Body — the transcript */}
        <div ref={bodyRef} className="ask-scroll min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-3">
          {configured === false && (
            <p className="rounded-xl bg-blue-50 px-3 py-2.5 text-sm leading-relaxed text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
              Ask isn’t switched on yet — the server needs an OpenAI API key before questions can be answered.
            </p>
          )}

          {empty && (
            <div className="space-y-3 py-1">
              <p className="flex items-center justify-center gap-2 pb-0.5 pt-2 text-base font-semibold text-navy-800 dark:text-slate-100">
                <AskMark className="h-[18px] w-[18px] shrink-0 text-brand dark:text-brand-light" />
                Query your usage data
              </p>
              <div className="space-y-1.5">
                {/* Named as a shortcut, not a menu. A block of buttons with no
                    qualifier reads as "these are your only options", which is
                    the opposite of the point. */}
                <p className="px-0.5 text-xs text-slate-400 dark:text-slate-500">
                  Try one of these, or type your own question below
                </p>
                {examples.map((ex) => (
                  <button
                    key={ex}
                    type="button"
                    onClick={() => submit(null, ex)}
                    className="group flex w-full items-center justify-between gap-2 rounded-xl border border-slate-900/5 bg-slate-50/80 px-3.5 py-3 text-left text-sm text-slate-600 transition-all hover:-translate-y-px hover:border-brand/30 hover:bg-brand/5 hover:text-brand hover:shadow-sm dark:border-white/5 dark:bg-white/5 dark:text-slate-300 dark:hover:border-brand/40 dark:hover:bg-brand/15 dark:hover:text-brand-light"
                  >
                    <span>{ex}</span>
                    <svg
                      className="h-3.5 w-3.5 shrink-0 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100"
                      viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
                    >
                      <path d="M4 10h11M11 6l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          )}

          {turns.map((t, i) => (
            <div
              key={`${i}-${t.question}`}
              className={`ask-turn-in ${i > 0 ? 'border-t border-slate-900/5 pt-4 dark:border-white/5' : ''}`}
            >
              <Turn
                turn={t}
                onExpand={() => expandTurn(i)}
                onSuggest={(q) => submit(null, q)}
                // Only the newest turn offers follow-ups — on older turns they
                // are stale advice and just clutter the transcript.
                showSuggestions={i === turns.length - 1 && !pending && !busy}
              />
            </div>
          ))}

          {pending && (
            <div className={turns.length ? 'space-y-2.5 border-t border-slate-900/5 pt-4 dark:border-white/5' : 'space-y-2.5'}>
              <div className="flex justify-end">
                <p className="max-w-[85%] rounded-2xl rounded-br-md bg-brand/10 px-3.5 py-2 text-sm text-brand opacity-70 dark:bg-brand/20 dark:text-brand-light">
                  {pending}
                </p>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-slate-500 dark:text-slate-400">
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-brand/30 border-t-brand" />
                Reading the usage data…
              </div>
              <div className="space-y-1.5">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-7 animate-pulse rounded-lg bg-slate-100 dark:bg-white/5"
                    style={{ animationDelay: `${i * 120}ms`, opacity: 1 - i * 0.25 }}
                  />
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Composer */}
        <form
          onSubmit={submit}
          className="shrink-0 border-t border-slate-900/5 bg-white/40 px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.03]"
        >
          <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-1.5 shadow-sm transition-all focus-within:border-brand/50 focus-within:ring-2 focus-within:ring-brand/15 dark:border-white/10 dark:bg-white/5">
            <input
              ref={inputRef}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={500}
              disabled={busy}
              placeholder={turns.length ? 'Ask a follow-up…' : 'Ask about your usage data…'}
              className="min-w-0 flex-1 bg-transparent py-1.5 text-sm text-slate-700 outline-none placeholder:text-slate-400 disabled:opacity-60 dark:text-slate-200 dark:placeholder:text-slate-500"
            />
            {!question && !busy && (
              <kbd className="hidden shrink-0 rounded border border-slate-200 px-1.5 py-0.5 font-sans text-[11px] font-medium text-slate-400 sm:block dark:border-white/10 dark:text-slate-500">
                {isMac ? '⌘K' : 'Ctrl K'}
              </kbd>
            )}
            <button
              type="submit"
              disabled={busy || !question.trim()}
              aria-label="Send"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-brand-gradient text-white shadow-brand transition-all hover:scale-105 active:scale-95 disabled:scale-100 disabled:opacity-30 disabled:shadow-none"
            >
              <SendIcon />
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
